import * as React from 'react';
import { LogLevel } from "../logger/logger";

declare let window: Window & {
    uet: Function,
    ues: Function,
    uex: Function,
    ue: {
        count: (name: string, value?: number, options?: { bf: number }) => number | void,
        tag: (name: string) => void
    },
    ueLogError: (error: Error, info: additionalErrorInfo) => void
}

export interface additionalErrorInfo {
    logLevel?: LogLevel.FATAL | LogLevel.ERROR | LogLevel.WARN,
    attribution?: string,  // This will be used to whitelist metrics with breakdown by attribution
    message?: string       // This will be added to the start of the Error object
}

export interface csmMarkerSwitches {
    isAtf: boolean,
    isCf: boolean
}

export enum CSMMarkers {
    AboveTheFold = 'af',
    CriticalFeature = 'cf',
    Functional = 'fn',
    BodyBegin = 'bb',
    BodyEnd = 'be',
    BackForward = 'bft',
    Experiment1 = 'x1',
    Experiment2 = 'x2',
    Experiment3 = 'x3'
}

const getInlineUetScript = (markerName: CSMMarkers) => {
    const dangerousHtmlObj = { __html: `<script>if (typeof window.uet === 'function') { window.uet('${markerName}'); }</script>` }

    return (<span style={{display: 'none'}} dangerouslySetInnerHTML={ dangerousHtmlObj } />);
}

export const InlineCsmMarker = (props: {marker: CSMMarkers}) => {
    return getInlineUetScript(props.marker)
}

export const clickToATF = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.AboveTheFold)
    }
}

export const setFunctional = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.Functional)
    }
}

export const InlineClickToATF = () => {
    return getInlineUetScript(CSMMarkers.AboveTheFold)
}

export const clickToCF = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.CriticalFeature)
    }
}

export const InlineClickToCF = () => {
    return getInlineUetScript(CSMMarkers.CriticalFeature)
}

export const GetInlineAfCfMarker = (props: csmMarkerSwitches) => {
    return (<>{props.isAtf ? (<InlineClickToATF />) : (<></>)}{props.isCf ? (<InlineClickToCF />) : (<></>)}</>) 
}

// https://w.amazon.com/index.php/ClientSideMetrics/UserDocs/ExperimentalPageMarkers
export const clickToX1 = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.Experiment1)
    }
}

export const clickToX2 = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.Experiment2)
    }
}

export const clickToX3 = () => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.Experiment3)
    }
}

// https://w.amazon.com/index.php/ClientSideMetrics/UserDocs/Instrumentation/CSMforAJAX
// https://w.amazon.com/index.php/ClientSideMetrics/UserDocs/Instrumentation/CSMforAJAX/Scenario2NewPage
// https://w.amazon.com/bin/view/ClientSideMetrics/UserDocs/Instrumentation/InstrumentingWidgets
const csmWidgetOptions = { wb: 1 }

export const setTimeOfClick = (scope: string) => {
    if (window.uet) {
        window.uet('tc', scope)
    }
}

export const setAjaxStart = (scope: string) => {
    if (window.ues) {
        window.ues('t0', scope, new Date())
        window.ues('ctb', scope, '1')
    }
}

export const setClientTimeBased = (scope: string) => {
    if (window.ues) {
        window.ues('ctb', scope, '1')
    }
}

export const setPageLoadStart = (scope: string) => {
    if (window.ues) {
        window.ues('t0', scope, new Date())
    }
}

export const setAjaxRequestID = (scope: string, requestID: string) => {
    if (window.ues) {
        window.ues('id', scope, requestID)
    }
}

export const setMovingBackForward = (scope: string, useWidgetFlag: boolean) => {
    if (window.uet) {
        window.uet(CSMMarkers.BackForward, scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

export const setAjaxBodyBegin = (scope: string, useWidgetFlag: boolean) => {
    if (window.uet) {
        window.uet(CSMMarkers.BodyBegin, scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

export const setAjaxAF = (scope: string, useWidgetFlag: boolean) => {
    if (window.uet) {
        window.uet(CSMMarkers.AboveTheFold, scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

export const setAjaxCF = (scope: string, useWidgetFlag: boolean) => {
    if (window.uet) {
        window.uet(CSMMarkers.CriticalFeature, scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

export const setAjaxBodyEnd = (scope: string, useWidgetFlag: boolean) => {
    if (window.uet) {
        window.uet(CSMMarkers.BodyEnd, scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

// 'fn' does not support { wb: 1 }. TTI is only on "page"-level not on "widget"-level.
// See https://w.amazon.com/bin/view/ClientSideMetrics/UserDocs/TTI/#HSpecialconsiderations
export const setAjaxFunctional = (scope: string) => {
    if (typeof window.uet === 'function') {
        window.uet(CSMMarkers.Functional, scope)
    }
}

export const sendMetricsBack = (scope: string, useWidgetFlag: boolean) => {
    if (window.uex) {
        window.uex('ld', scope, useWidgetFlag ? csmWidgetOptions : undefined)
    }
}

export const counter = (counterName: string, value: number, increment: boolean): void => {
    if (typeof window.ue !== 'undefined' && typeof window.ue.count === 'function') {
        if (increment) {
            // Increment value
            window.ue.count(counterName, (window.ue.count(counterName) || 0) + value)
        } else {
            // Set value
            window.ue.count(counterName, value)
        }
    }
}

export const addTag = (tag: string = '') => {
    if (typeof window.ue !== 'undefined' && typeof window.ue.tag === 'function') {
        window.ue.tag(tag)
    }
}

export const logJSError = (error: Error, info: additionalErrorInfo) => {
    if (typeof window !== 'undefined' && window.ueLogError) {
        window.ueLogError(error, info)
    }
}
